import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import FormField from "../FormField/FormField";
import LandingCopyEditor from "./LandingCopyEditor";
import "./LandingPageSeo.css";
import { SEO_LIMITS } from "../../Modules/Category/constants";

// Arabic for "a short summary of this …", per kind of page.
const AR_SUMMARY_PLACEHOLDER = {
  category: "ملخص قصير لهذا القسم",
  subcategory: "ملخص قصير لهذا القسم",
  brand: "ملخص قصير لهذه الماركة",
};

const NOUN = { category: "category", subcategory: "subcategory", brand: "brand" };

const CharCounter = ({ value = "", max }) => {
  const len = value?.length || 0;
  const state = len > max ? "over" : len > max * 0.9 ? "near" : "ok";
  return (
    <span className={`nb-counter nb-counter--${state}`}>
      {len}/{max}
    </span>
  );
};

/**
 * "Search & SEO" on the category, sub-category and brand forms: the copy of
 * that row's own page on the storefront, in both languages, plus whether
 * Google may list it. The fields are the form's flat ``lp_`` values (see
 * landingPageFields); the form gathers them into ``landing_page`` on save.
 *
 * ``landingPage`` is the page the record came back with, or null when the
 * row has none yet (inactive, or not saved). ``previewName`` and
 * ``previewArName`` are what a blank field falls back to on the storefront,
 * shown as placeholders.
 */
export default function LandingPageSeoSection({
  formik,
  readOnly,
  step,
  kind,
  landingPage,
  previewName,
  previewArName,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { values } = formik;
  const noun = NOUN[kind];

  const input = (field, extra = {}) => ({
    id: field,
    name: field,
    value: values[field],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
    disabled: readOnly,
    ...extra,
  });

  return (
    <section className={`nb-section nb-section--seo ${open ? "is-open" : ""}`}>
      <div className="nb-section__head">
        <span className="nb-section__step">{step}</span>
        <div>
          <h2 className="nb-section__title">
            {t("Search & SEO")}
            <span className="nb-section__optional">{t("Optional")}</span>
          </h2>
          <p className="nb-section__hint">
            {t(`The wording of this ${noun}'s own page on the storefront. Leave a field empty and it writes itself from the ${noun} name.`)}
          </p>
        </div>
        <button
          type="button"
          className="nb-section__toggle"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          disabled={readOnly}
        >
          {open ? t("Hide") : (
            <>
              <Plus size={13} aria-hidden="true" />
              {t("Add SEO details")}
            </>
          )}
        </button>
      </div>
      {open && (
        <div className="nb-section__body">
          {landingPage ? (
            <p className="nb-section__hint">
              {t(`This ${noun}'s page is live at`)}{" "}
              <strong>/{landingPage.path}</strong>
              {". "}
              {t(`The address is fixed when the ${noun} is created and does not move if you rename it, so links and Google rankings survive a rename.`)}
            </p>
          ) : (
            <p className="nb-section__hint">
              {t(`Save this ${noun} as Active and its page is created automatically — anything you write here is kept and applied to it.`)}
            </p>
          )}

          <label className="nb-switchrow">
            <input
              id="lp_is_indexable"
              name="lp_is_indexable"
              type="checkbox"
              checked={Boolean(values.lp_is_indexable)}
              onChange={(e) => formik.setFieldValue("lp_is_indexable", e.target.checked)}
              disabled={readOnly}
            />
            <span className="nb-switchrow__track" aria-hidden="true">
              <span className="nb-switchrow__thumb" />
            </span>
            <span className="nb-switchrow__text">
              <strong>{t("Show this page on Google")}</strong>
              <span>
                {t("Turn off to keep the page working for shoppers while keeping it out of search results. A page with no products stays out on its own.")}
              </span>
            </span>
          </label>

          <p className="lp-group">{t("Shown on Google")}</p>
          <div className="nb-grid-2">
            <FormField
              name="lp_seo_title"
              label={t("Google title (English)")}
              hint={t("Shown as the page title on Google.")}
              labelRight={<CharCounter value={values.lp_seo_title} max={SEO_LIMITS.META_TITLE} />}
            >
              <input
                {...input("lp_seo_title")}
                type="text"
                className="nb-input"
                placeholder={`${previewName} | Nysmah`}
                maxLength={SEO_LIMITS.META_TITLE + 20}
              />
            </FormField>
            <FormField
              name="lp_ar_seo_title"
              label={t("Google title (Arabic)")}
              hint={t("Arabic shoppers never see the English one.")}
              labelRight={<CharCounter value={values.lp_ar_seo_title} max={SEO_LIMITS.META_TITLE} />}
            >
              <input
                {...input("lp_ar_seo_title")}
                type="text"
                className="nb-input"
                dir="rtl"
                placeholder={previewArName ? `${previewArName} | نسمة` : "نسمة"}
                maxLength={SEO_LIMITS.META_TITLE + 20}
              />
            </FormField>
          </div>

          <div className="nb-grid-2">
            <FormField
              name="lp_meta_description"
              label={t("Google description (English)")}
              hint={t("Summary shown below the title in Google results.")}
              labelRight={
                <CharCounter value={values.lp_meta_description} max={SEO_LIMITS.META_DESCRIPTION} />
              }
            >
              <textarea
                {...input("lp_meta_description")}
                className="nb-input nb-textarea"
                rows={3}
                placeholder={t(`A short summary of this ${noun}`)}
                maxLength={SEO_LIMITS.META_DESCRIPTION + 40}
              />
            </FormField>
            <FormField
              name="lp_ar_meta_description"
              label={t("Google description (Arabic)")}
              labelRight={
                <CharCounter value={values.lp_ar_meta_description} max={SEO_LIMITS.META_DESCRIPTION} />
              }
            >
              <textarea
                {...input("lp_ar_meta_description")}
                className="nb-input nb-textarea"
                rows={3}
                dir="rtl"
                placeholder={AR_SUMMARY_PLACEHOLDER[kind]}
                maxLength={SEO_LIMITS.META_DESCRIPTION + 40}
              />
            </FormField>
          </div>

          <p className="lp-group">{t("Shown on the page")}</p>
          <div className="nb-grid-2">
            <FormField name="lp_h1" label={t("Page heading (English)")}>
              <input {...input("lp_h1")} type="text" className="nb-input" placeholder={previewName} />
            </FormField>
            <FormField name="lp_ar_h1" label={t("Page heading (Arabic)")}>
              <input
                {...input("lp_ar_h1")}
                type="text"
                className="nb-input"
                dir="rtl"
                placeholder={previewArName}
              />
            </FormField>
          </div>
          <div className="nb-grid-2">
            <LandingCopyEditor
              name="lp_intro_html"
              lang="en"
              value={values.lp_intro_html}
              onChange={(html) => formik.setFieldValue("lp_intro_html", html)}
              kind={kind}
              readOnly={readOnly}
              heading={values.lp_h1 || previewName}
              currentPath={landingPage?.path}
            />
            <LandingCopyEditor
              name="lp_ar_intro_html"
              lang="ar"
              value={values.lp_ar_intro_html}
              onChange={(html) => formik.setFieldValue("lp_ar_intro_html", html)}
              kind={kind}
              readOnly={readOnly}
              heading={values.lp_ar_h1 || previewArName || previewName}
              currentPath={landingPage?.path}
            />
          </div>
        </div>
      )}
    </section>
  );
}
